import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { SeriesChart } from '../components/SeriesChart';
import { DataTable, type Column } from '../components/DataTable';
import { fmtNum, fmtRelative } from '../lib/format';
import type { ScannerFeedbackDto, ScannerOverviewDto } from '../api/types';

type LangRow = ScannerOverviewDto['byLanguage'][number];
type VersionRow = ScannerOverviewDto['byVersion'][number];

// Sentinel query value for scans with a null app_version (pre-instrumentation
// builds). Kept in sync with the backend's `?version=unknown` contract.
const UNKNOWN_VERSION = 'unknown';

/** Human label for a version row / dropdown option. null → "unknown (pre-2.x)". */
function versionLabel(v: string | null): string {
  return v == null || v === '' ? 'unknown (pre-2.x)' : `v${v}`;
}

export function ScannerScreen() {
  // "" = All versions. Otherwise an appVersion string, or UNKNOWN_VERSION for null.
  const [version, setVersion] = useState('');
  // Remember the fullest byVersion list we've seen so the dropdown options stay
  // stable even while a scoped fetch is in flight.
  const versionOptions = useRef<VersionRow[]>([]);

  const overview = useQuery({
    queryKey: ['scanner-overview', version || 'all'],
    queryFn: () => insightsApi.scannerOverview(version || undefined),
  });
  if (overview.data && overview.data.byVersion.length >= versionOptions.current.length) {
    versionOptions.current = overview.data.byVersion;
  }
  const feedback = useQuery({
    queryKey: ['scanner-feedback', 50],
    queryFn: () => insightsApi.scannerFeedback(50),
  });

  const o = overview.data;
  const f = feedback.data;

  const seriesPoints = (o?.series ?? []).map((p) => ({
    label: shortDay(p.date),
    value: p.scans,
    line: p.matchRatePct,
  }));

  const langColumns: Column<LangRow>[] = [
    {
      key: 'language',
      header: 'Language',
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>{r.language || '—'}</span>,
    },
    { key: 'scans', header: 'Scans', sortKey: 'scans', align: 'right', render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtNum(r.scans)}</span> },
    { key: 'matched', header: 'Matched', sortKey: 'matched', align: 'right', render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>{fmtNum(r.matched)}</span> },
    {
      key: 'matchRatePct',
      header: 'Match rate',
      sortKey: 'matchRatePct',
      align: 'right',
      render: (r) => <Pill tone={rateTone(r.matchRatePct)} size="sm">{r.matchRatePct.toFixed(1)}%</Pill>,
    },
  ];

  const versionColumns: Column<VersionRow>[] = [
    {
      key: 'appVersion',
      header: 'App version',
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{versionLabel(r.appVersion)}</span>,
    },
    { key: 'scans', header: 'Scans', sortKey: 'scans', align: 'right', render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtNum(r.scans)}</span> },
    { key: 'matched', header: 'Matched', sortKey: 'matched', align: 'right', hideMobile: true, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>{fmtNum(r.matched)}</span> },
    {
      key: 'matchRatePct',
      header: 'Match rate',
      sortKey: 'matchRatePct',
      align: 'right',
      render: (r) => <Pill tone={rateTone(r.matchRatePct)} size="sm">{r.matchRatePct.toFixed(1)}%</Pill>,
    },
  ];

  const missedColumns: Column<ScannerFeedbackDto['topMissedCards'][number]>[] = [
    {
      key: 'card',
      header: 'Card',
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{r.name ?? r.cardId}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.setName ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'corrections',
      header: 'Corrections',
      sortKey: 'corrections',
      align: 'right',
      render: (r) => <Pill tone="warn" size="sm">{fmtNum(r.corrections)}</Pill>,
    },
  ];

  if (overview.error) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <Card padding={20}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>{(overview.error as Error).message}</span>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
          App version
        </span>
        <select
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          style={{
            appearance: 'none',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line-strong)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink)',
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            padding: '8px 12px',
            cursor: 'pointer',
          }}
        >
          <option value="">All versions</option>
          {versionOptions.current.map((v) => {
            const val = v.appVersion == null || v.appVersion === '' ? UNKNOWN_VERSION : v.appVersion;
            return (
              <option key={val} value={val}>
                {versionLabel(v.appVersion)} · {fmtNum(v.scans)} scans
              </option>
            );
          })}
        </select>
        {overview.isFetching && (
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading…</span>
        )}
        {version !== '' && (
          <Pill tone="brand" size="sm">
            scoped to {version === UNKNOWN_VERSION ? 'unknown (pre-2.x)' : `v${version}`}
          </Pill>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <KpiCard
          label="Match rate · 30d"
          value={o?.last30d.matchRatePct ?? 0}
          format={(n) => `${n.toFixed(1)}%`}
          decimals={1}
          accent
        />
        <KpiCard label="Scans · 30d" value={o?.last30d.scans ?? 0} />
        <KpiCard label="Matched · 30d" value={o?.last30d.matched ?? 0} />
        <KpiCard label="No match · 30d" value={o?.last30d.noMatch ?? 0} />
      </div>

      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Daily scans &amp; match rate
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', display: 'inline-flex', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand-soft)' }} /> scans
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand-2, var(--brand))' }} /> match rate %
            </span>
          </span>
        </div>
        {overview.isFetching && seriesPoints.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
        ) : seriesPoints.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No scan activity yet.</div>
        ) : (
          <SeriesChart
            data={seriesPoints}
            height={260}
            valueLabel="scans"
            lineLabel="match rate"
            linePercent
            lineFmt={(n) => `${n.toFixed(1)}%`}
          />
        )}
      </Card>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Match rate by language</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Last 30 days · scans grouped by card language</div>
        </div>
        <DataTable
          rows={o?.byLanguage ?? []}
          columns={langColumns}
          rowKey={(r) => r.language}
          loading={overview.isFetching}
          pageSize={25}
          emptyState="No language breakdown yet."
        />
      </Card>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Match rate by app version</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Compare accuracy across builds · "unknown" = pre-instrumentation scans (older builds)
          </div>
        </div>
        <DataTable
          rows={o?.byVersion ?? []}
          columns={versionColumns}
          rowKey={(r) => r.appVersion ?? UNKNOWN_VERSION}
          loading={overview.isFetching}
          pageSize={25}
          emptyState="No version breakdown yet."
        />
      </Card>

      {/* Feedback sentiment */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 14 }}>Scan feedback sentiment</div>
          <SentimentRow label="Last 30 days" data={f?.sentiment.last30d} />
          <div style={{ height: 12 }} />
          <SentimentRow label="All time" data={f?.sentiment.allTime} />
        </Card>
        <Card padding={0}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>Most missed cards</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Ranked by user corrections</div>
          </div>
          <DataTable
            rows={f?.topMissedCards ?? []}
            columns={missedColumns}
            rowKey={(r) => r.cardId}
            loading={feedback.isFetching}
            pageSize={10}
            emptyState="No corrections logged yet."
          />
        </Card>
      </div>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Recent negative feedback</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Latest 👎 comments from scanning</div>
        </div>
        <div style={{ padding: '8px 0' }}>
          {feedback.isFetching && !f && (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
          )}
          {f && f.recentNegativeComments.length === 0 && (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No negative comments — nice.</div>
          )}
          {(f?.recentNegativeComments ?? []).map((c) => (
            <div key={c.id} style={{ padding: '12px 20px', borderTop: '1px solid var(--line)' }}>
              <div style={{ fontSize: 13, color: 'var(--ink)', marginBottom: 6 }}>{c.comment}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-3)' }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtRelative(c.createdAt)}</span>
                {c.platform && <Pill size="sm">{c.platform}</Pill>}
                {c.appVersion && <Pill size="sm">v{c.appVersion}</Pill>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SentimentRow({
  label,
  data,
}: {
  label: string;
  data?: { up: number; down: number; total: number; upRatePct: number };
}) {
  const up = data?.up ?? 0;
  const down = data?.down ?? 0;
  const total = data?.total ?? 0;
  const upPct = data?.upRatePct ?? 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
          {upPct.toFixed(1)}% 👍 <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· {fmtNum(total)} total</span>
        </span>
      </div>
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'var(--bg-sunken)' }}>
        <div style={{ width: `${total > 0 ? (up / total) * 100 : 0}%`, background: 'var(--good)' }} />
        <div style={{ width: `${total > 0 ? (down / total) * 100 : 0}%`, background: 'var(--bad)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
        <span>👍 {fmtNum(up)}</span>
        <span>👎 {fmtNum(down)}</span>
      </div>
    </div>
  );
}

function rateTone(pct: number): 'good' | 'warn' | 'bad' {
  if (pct >= 85) return 'good';
  if (pct >= 60) return 'warn';
  return 'bad';
}

/** "Jul 7" from an ISO date string. */
function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
